export type BrowserNavigateResolution =
  | { kind: 'url'; url: string }
  | { kind: 'recovery'; message: string };

type CurrentUrlReader = () => Promise<string | undefined>;

export function isNavigateDownloadStartingError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /\bgoto:\s+Download is starting\b/.test(message);
}

export function formatNavigateDownloadStartingRecovery(): string {
  return [
    'Error: browser_navigate started a file download instead of loading a page.',
    'Do not retry browser_navigate for the same URL. Call browser_list_downloads to find the captured file, then use browser_read_download or browser_pdf_read to inspect it.',
  ].join(' ');
}

function isRelativeNavigateTarget(url: string): boolean {
  const trimmed = url.trim();
  return (
    trimmed.startsWith('/') ||
    trimmed.startsWith('./') ||
    trimmed.startsWith('../') ||
    trimmed.startsWith('?') ||
    trimmed.startsWith('#')
  );
}

function isHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function findUnresolvedNavigatePlaceholders(url: string): string[] {
  const variableRefs = Array.from(url.matchAll(/\$var\.([A-Za-z0-9_.]+)/g)).map(
    (match) => `$var.${match[1]!}`
  );
  const macroRefs = Array.from(url.matchAll(/\$[A-Z][A-Z0-9_]*\([^)]*\)/g)).map(
    (match) => match[0]
  );
  return [...new Set([...variableRefs, ...macroRefs])];
}

function formatRelativeNavigateRecovery(url: string, currentUrl: string | undefined): string {
  const baseDescription = currentUrl
    ? `current page URL is "${currentUrl}"`
    : 'current page URL is unavailable';
  return [
    `Error: browser_navigate received relative URL "${url}", but the ${baseDescription}.`,
    'Use an absolute http(s) URL, or call browser_snapshot first if the current page needs to be inspected before navigating.',
  ].join(' ');
}

function formatUnresolvedNavigatePlaceholderRecovery(url: string, placeholders: string[]): string {
  const placeholderList = placeholders.join(', ');
  return [
    `Error: browser_navigate failed because URL "${url}" contains unresolved placeholders: ${placeholderList}.`,
    'Capture the value first, pass a literal http(s) URL, or use url:"back".',
  ].join(' ');
}

export async function resolveBrowserNavigateUrl(
  url: string,
  getCurrentUrl: CurrentUrlReader
): Promise<BrowserNavigateResolution> {
  const trimmedUrl = url.trim();
  const unresolvedPlaceholders = findUnresolvedNavigatePlaceholders(trimmedUrl);
  if (unresolvedPlaceholders.length > 0) {
    return {
      kind: 'recovery',
      message: formatUnresolvedNavigatePlaceholderRecovery(trimmedUrl, unresolvedPlaceholders),
    };
  }

  if (!isRelativeNavigateTarget(trimmedUrl)) {
    return { kind: 'url', url: trimmedUrl };
  }

  const currentUrl = await getCurrentUrl().catch(() => undefined);
  if (!currentUrl || !isHttpUrl(currentUrl)) {
    return { kind: 'recovery', message: formatRelativeNavigateRecovery(trimmedUrl, currentUrl) };
  }

  try {
    return { kind: 'url', url: new URL(trimmedUrl, currentUrl).toString() };
  } catch {
    return { kind: 'recovery', message: formatRelativeNavigateRecovery(trimmedUrl, currentUrl) };
  }
}
